import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { MobilisationMember } from '../api'

/**
 * The people in a mobilisation, with what can be done about each: let them in, refuse them (with
 * a reason), or send their code again. Shared by the recovery set's panel and "Confirm my team".
 * Which buttons a row gets is `mayDecide` - the screen's best guess at what the server will
 * allow; the server decides (lib/mobilisation.js mayDecide).
 */
export function MemberList({ members, mayDecide, busy, onConfirm, onRefuse, onResend }: {
    members: MobilisationMember[]
    mayDecide: (member: MobilisationMember) => boolean
    busy: boolean
    onConfirm: (member: MobilisationMember) => void
    onRefuse: (member: MobilisationMember, reason: string) => void
    onResend: (member: MobilisationMember) => void
}) {
    const { t } = useTranslation()
    const [refusing, setRefusing] = useState<number | null>(null)
    const [reason, setReason] = useState('')
    if (members.length === 0) return <p className="text-sm text-muted-foreground">{t('mobilisation.noMembers')}</p>
    return (
        <ul className="space-y-2">
            {members.map((member) => {
                const decide = mayDecide(member)
                return (
                    <li key={member.id} className="rounded-md border bg-card p-3 text-sm" data-testid="mobilisation-member" data-status={member.status} data-role={member.role}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                                <div className="font-medium">
                                    {member.name || member.email}
                                    {member.role !== 'member' && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[11px]">{t(`mobilisation.role.${member.role}`)}</span>}
                                </div>
                                <div className="text-xs text-muted-foreground">{member.email}{member.teamName ? ` · ${member.teamName}` : ''}</div>
                                <div className="mt-1 text-xs">
                                    <span className={member.status === 'enrolled' ? 'font-medium text-primary' : 'text-muted-foreground'}>{t(`mobilisation.status.${member.status}`)}</span>
                                    {!member.hasNumber && <span className="ml-2 text-destructive" data-testid="no-number">{t('mobilisation.noNumber')}</span>}
                                    {member.status === 'invited' && member.sms && (
                                        <span className={member.sms.failed ? 'ml-2 text-destructive' : 'ml-2 text-muted-foreground'} data-testid="sms-state">
                                            {t(`mobilisation.sms.${member.sms.status}`)}{member.phone ? ` (${member.phone})` : ''}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {decide && (
                                <div className="flex shrink-0 flex-wrap gap-2">
                                    {member.status === 'enrolled' && (
                                        <Button size="sm" onClick={() => onConfirm(member)} disabled={busy} aria-label={t('mobilisation.confirmNamed', { name: member.name || member.email })}>
                                            {t('mobilisation.confirm')}
                                        </Button>
                                    )}
                                    {member.status === 'invited' && member.hasNumber && (
                                        <Button size="sm" variant="outline" onClick={() => onResend(member)} disabled={busy}>{t('mobilisation.resend')}</Button>
                                    )}
                                    {(member.status === 'enrolled' || member.status === 'invited') && (
                                        <Button size="sm" variant="outline" onClick={() => { setRefusing(member.id); setReason('') }} disabled={busy}
                                            aria-label={t('mobilisation.refuseNamed', { name: member.name || member.email })}>
                                            {t('mobilisation.refuse')}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                        {refusing === member.id && (
                            <form
                                className="mt-2 flex gap-2" data-testid="refuse-form"
                                onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 10) { onRefuse(member, reason.trim()); setRefusing(null) } }}
                            >
                                <Input value={reason} maxLength={255} aria-label={t('mobilisation.refuseReason')} placeholder={t('mobilisation.refuseReason')} onChange={(e) => setReason(e.target.value)} />
                                <Button type="submit" size="sm" variant="destructive" disabled={busy || reason.trim().length < 10}>{t('mobilisation.refuse')}</Button>
                                <Button type="button" size="sm" variant="ghost" onClick={() => setRefusing(null)}>{t('common.cancel')}</Button>
                            </form>
                        )}
                    </li>
                )
            })}
        </ul>
    )
}
